/**
 * Which store this build talks to.
 *
 * One line, on purpose. Moving to Postgres means writing a sibling of
 * firestore.ts and changing the line below — everything above this file speaks
 * in paths and plain objects and does not know the difference.
 *
 * `db` here is the Firebase handle from src/firebase.ts, which also chooses
 * WHICH Firebase project — live or the test copy — from the build's own stamp.
 * Those are two separate questions and they stay in two separate files: which
 * kind of database, and which copy of it.
 */
import crypto from './hash';
import { supabase } from '../supabase';
import { supabaseBackend } from './supabase';
import { supabaseAccounts } from './supabase-auth';
import { idFrom } from './paths';
import type { Backend } from './types';
import type { Accounts } from './auth';

/*
 * POSTGRES, ON THE TEST COPY, FROM 7 AUGUST 2026.
 *
 * These two lines are the whole switch. Firebase is still deployed, still
 * holds the same data, and its implementation is still in this repository
 * beside the new one — going back is changing these lines and nothing else,
 * which is why the boundary was built before any of this.
 *
 * WHAT HAD TO BE TRUE FIRST, and was: 32 policies written and reviewed; 81
 * security cases passing against them, re-proved after they turned out to have
 * been proved against a token shape Supabase does not issue; the data moved
 * and counted table by table; and a conformance run against the real database
 * as a real signed-in person, which found four faults nothing else could.
 *
 * WHAT THIS CHANGES FOR PEOPLE. Sign-in moves to Supabase, so the first visit
 * after this needs a fresh sign-in link — a Firebase session is not a Supabase
 * one. Everybody's access, boards, roles and history are already there under
 * the same email address.
 */
export const backend: Backend = supabaseBackend(supabase, (kind, key) =>
  idFrom(kind, key, crypto.md5)
);

export const accounts: Accounts = supabaseAccounts(supabase);
export type { Account, Accounts, AuthError, AuthProblem } from './auth';
export type { Backend, BackendError, Rec, Unsubscribe, WithId, WithParent, Writable } from './types';
